import * as React from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { decodeJwt } from 'jose';
import { supabase } from './supabase';
import { setUnauthorizedHandler } from './rpc';

/** Claims the Supabase custom access token hook stamps onto every JWT. */
export interface PhinestClaims {
  sub?: string;
  email?: string;
  tenant_id?: string;
  tenant_role?: string;
  tenant_slug?: string;
  tenant_name?: string;
}

export interface AuthValue {
  loading: boolean;
  session: Session | null;
  user: User | null;
  claims: PhinestClaims | null;
  /** true when signed in but the token carries no workspace yet. */
  needsWorkspace: boolean;
  signInWithPassword(email: string, password: string): Promise<void>;
  signUp(email: string, password: string): Promise<{ needsConfirmation: boolean }>;
  signInWithLink(email: string, redirectTo?: string): Promise<void>;
  signOut(): Promise<void>;
  /** Pull a new token, e.g. after changing the active workspace. */
  refresh(): Promise<void>;
}

const AuthContext = React.createContext<AuthValue | null>(null);

function claimsOf(session: Session | null): PhinestClaims | null {
  if (!session?.access_token) return null;
  try {
    return decodeJwt(session.access_token) as PhinestClaims;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // A 401 from the API means the token is no longer good; ask Supabase for a new one.
  React.useEffect(() => {
    setUnauthorizedHandler(() => {
      void supabase.auth.refreshSession();
    });
  }, []);

  const value = React.useMemo<AuthValue>(() => {
    const claims = claimsOf(session);
    return {
      loading,
      session,
      user: session?.user ?? null,
      claims,
      needsWorkspace: !!session && !claims?.tenant_id,
      async signInWithPassword(email, password) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message);
      },
      async signUp(email, password) {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw new Error(error.message);
        return { needsConfirmation: !data.session };
      },
      async signInWithLink(email, redirectTo) {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: redirectTo ?? `${window.location.origin}/auth/callback` },
        });
        if (error) throw new Error(error.message);
      },
      async signOut() {
        const { error } = await supabase.auth.signOut({ scope: 'local' });
        if (error) throw new Error(error.message);
        queryClient.clear();
      },
      async refresh() {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) throw new Error(error.message);
        setSession(data.session);
      },
    };
  }, [session, loading, queryClient]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const v = React.useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
